import React, { useEffect, useState } from "react";
import { getSuperAdminSettings, updateSuperAdminSettings } from "../../services/superAdminApi";

const SuperAdminSettings: React.FC = () => {
  const [form, setForm] = useState<Record<string, any>>({});
  const [currencies, setCurrencies] = useState<Array<Record<string, any>>>([]);
  const [languages, setLanguages] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await getSuperAdminSettings();
        setForm(res.setting || {});
        setCurrencies(res.currencies || []);
        setLanguages(res.languages || []);
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load settings.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await updateSuperAdminSettings({
        company_name: form.company_name,
        company_email: form.company_email,
        company_phone: form.company_phone,
        contact_email: form.contact_email,
        address: form.address,
        website: form.website,
        currency_id: form.currency_id,
        timezone: form.timezone,
        date_format: form.date_format,
        time_format: form.time_format,
        locale: form.locale,
        sign_up_note: form.sign_up_note,
        terms_note: form.terms_note,
        map_option: form.map_option,
        map_key: form.map_key,
        google_calendar: form.google_calendar,
        google_client_id: form.google_client_id,
        google_client_secret: form.google_client_secret,
      });
      setSuccess("Settings updated successfully.");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to update settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Loading superadmin settings...</div>;
  }

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Superadmin Settings</h1>
        <p className="text-sm text-gray-500">Global application settings and platform configuration.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4 max-w-3xl">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-600">{success}</p>}

        <h2 className="text-sm font-semibold text-[#402B69]">General</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Company Name</label>
            <input
              value={form.company_name || ""}
              onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Company Email</label>
            <input
              value={form.company_email || ""}
              onChange={(e) => setForm((p) => ({ ...p, company_email: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Company Phone</label>
            <input
              value={form.company_phone || ""}
              onChange={(e) => setForm((p) => ({ ...p, company_phone: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Contact Email</label>
            <input
              value={form.contact_email || ""}
              onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Website</label>
            <input
              value={form.website || ""}
              onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Address</label>
            <input
              value={form.address || ""}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <h2 className="text-sm font-semibold text-[#402B69] pt-2">Localization</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Currency</label>
            <select
              value={form.currency_id || ""}
              onChange={(e) => setForm((p) => ({ ...p, currency_id: Number(e.target.value) || "" }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select currency</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.id}>
                  {currency.currency_name || "Currency"} ({currency.currency_code || "-"})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Language</label>
            <select
              value={form.locale || ""}
              onChange={(e) => setForm((p) => ({ ...p, locale: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select language</option>
              {languages.map((language) => (
                <option key={language.id} value={language.language_code}>
                  {language.language_name || language.language_code || "Language"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Timezone</label>
            <input
              value={form.timezone || ""}
              onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date Format</label>
            <input
              value={form.date_format || ""}
              onChange={(e) => setForm((p) => ({ ...p, date_format: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Time Format</label>
            <input
              value={form.time_format || ""}
              onChange={(e) => setForm((p) => ({ ...p, time_format: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <h2 className="text-sm font-semibold text-[#402B69] pt-2">Integration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Map Option</label>
            <input
              value={form.map_option || ""}
              onChange={(e) => setForm((p) => ({ ...p, map_option: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Map Key</label>
            <input
              value={form.map_key || ""}
              onChange={(e) => setForm((p) => ({ ...p, map_key: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Google Calendar</label>
            <input
              value={form.google_calendar || ""}
              onChange={(e) => setForm((p) => ({ ...p, google_calendar: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Google Client ID</label>
            <input
              value={form.google_client_id || ""}
              onChange={(e) => setForm((p) => ({ ...p, google_client_id: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Google Client Secret</label>
            <input
              value={form.google_client_secret || ""}
              onChange={(e) => setForm((p) => ({ ...p, google_client_secret: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <h2 className="text-sm font-semibold text-[#402B69] pt-2">Notes</h2>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Sign Up Note</label>
            <textarea
              value={form.sign_up_note || ""}
              onChange={(e) => setForm((p) => ({ ...p, sign_up_note: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Terms Note</label>
            <textarea
              value={form.terms_note || ""}
              onChange={(e) => setForm((p) => ({ ...p, terms_note: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px]"
            />
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default SuperAdminSettings;

